import Phaser from "phaser";
import type { CharacterGridSpec } from "@/character/grid";
import {
  attackAnimationFrameRange,
  idleAnimationFrameRange,
  runAnimationFrameRange,
  sitAnimationFrameRange,
} from "@/character/spritesheet";
import { getGameSettings } from "@/game/model/game-settings";

function clampFrameRange(
  scene: Phaser.Scene,
  textureKey: string,
  start: number,
  end: number,
): { start: number; end: number } {
  const frameTotal = scene.textures.get(textureKey).frameTotal;
  const max = Math.max(0, frameTotal - 1);
  const s = Math.max(0, Math.min(start, max));
  const e = Math.max(s, Math.min(end, max));
  return { start: s, end: e };
}

/** Register idle / run / attack anims for a uniform spritesheet texture. */
export function registerCharacterSceneAnims(
  scene: Phaser.Scene,
  textureKey: string,
  prefix: string,
  grid: CharacterGridSpec,
): void {
  if (!scene.textures.exists(textureKey)) return;

  if (!scene.anims.exists(`${prefix}idle`)) {
    const raw = idleAnimationFrameRange(grid);
    const { start, end } = clampFrameRange(scene, textureKey, raw.start, raw.end);
    scene.anims.create({
      key: `${prefix}idle`,
      frames: scene.anims.generateFrameNumbers(textureKey, { start, end }),
      frameRate: 8,
      repeat: -1,
    });
  }
  if (!scene.anims.exists(`${prefix}run`)) {
    const raw = runAnimationFrameRange(grid);
    const { start, end } = clampFrameRange(scene, textureKey, raw.start, raw.end);
    scene.anims.create({
      key: `${prefix}run`,
      frames: scene.anims.generateFrameNumbers(textureKey, { start, end }),
      frameRate: 12,
      repeat: -1,
    });
  }
  if (!scene.anims.exists(`${prefix}attack`)) {
    const raw = attackAnimationFrameRange(grid);
    const { start, end } = clampFrameRange(scene, textureKey, raw.start, raw.end);
    scene.anims.create({
      key: `${prefix}attack`,
      frames: scene.anims.generateFrameNumbers(textureKey, { start, end }),
      frameRate: getGameSettings().playerAttackAnimFps,
      repeat: 0,
    });
  }
  if (!scene.anims.exists(`${prefix}sit`)) {
    const raw = sitAnimationFrameRange(grid);
    const { start, end } = clampFrameRange(scene, textureKey, raw.start, raw.end);
    scene.anims.create({
      key: `${prefix}sit`,
      frames: scene.anims.generateFrameNumbers(textureKey, { start, end }),
      frameRate: 8,
      repeat: -1,
    });
  }
}
